"use client";

/**
 * AttachFilesButton — the paperclip button in the prompt input footer that
 * opens the native file picker.
 *
 * It calls ``openFileDialog()`` from the {@link usePromptInputAttachments}
 * context, which clicks the hidden ``<input type="file">`` rendered by
 * ``PromptInput``.  The actual file selection, validation, blob-URL
 * creation, and drag-drop/paste handling all live in ``PromptInput`` — this
 * is just the visible affordance.
 */

import {
  PromptInputButton,
  usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input";
import { PaperclipIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export type AttachFilesButtonProps = {
  /** Tooltip text (defaults to "Attach files"). */
  tooltip?: string;
  /** Optional badge count override. By default the attachment count is shown
   *  when there is at least one attached file. */
  showCount?: boolean;
};

/**
 * Paperclip button that opens the file picker.
 *
 * Render inside ``<PromptInput>`` (or ``<PromptInputProvider>``) so the
 * attachments context is available.
 */
export function AttachFilesButton({
  tooltip,
  showCount = true,
}: AttachFilesButtonProps) {
  const { t } = useTranslation();
  const resolvedTooltip = tooltip ?? t("input.attachFiles");
  const { openFileDialog, files } = usePromptInputAttachments();

  const handleClick = useCallback(() => {
    openFileDialog();
  }, [openFileDialog]);

  const count = files.length;

  return (
    <PromptInputButton
      onClick={handleClick}
      tooltip={resolvedTooltip}
      aria-label={resolvedTooltip}
      data-testid="attach-files-button"
      size={showCount && count > 0 ? "sm" : "icon-sm"}
    >
      <PaperclipIcon className="size-4" />
      {showCount && count > 0 && (
        <span
          className="ml-0.5 text-xs tabular-nums text-muted-foreground"
          data-testid="attachment-count"
        >
          {count}
        </span>
      )}
    </PromptInputButton>
  );
}
